import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, Check, X, Trophy, Ban } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const MEDALS = ["🥇", "🥈", "🥉"];

export default function AdminFranchises() {
    const [items, setItems] = useState([]);
    const [leaders, setLeaders] = useState([]);

    const load = async () => {
        try { const { data } = await api.get("/admin/franchises"); setItems(data.franchises || []); }
        catch { /* ignore */ }
        try { const { data } = await api.get("/admin/franchises/leaderboard"); setLeaders(data.leaderboard || []); }
        catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const act = async (id, action) => {
        try {
            const { data } = await api.post(`/admin/franchises/${id}/${action}`);
            toast.success(action === "approve" ? `Approved · code ${data.franchise_code}` : `Dealer ${action}d`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    };

    const revoke = async (id, name) => {
        if (!window.confirm(`Revoke the dealer license for ${name}? This disables their referral code, cancels platform auto-pay, and terminates the license.`)) return;
        const reason = window.prompt("Reason for revocation (optional):", "") || "";
        try {
            await api.post(`/admin/franchises/${id}/revoke`, { reason });
            toast.success("Dealer license revoked");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
    };

    return (
        <div className="space-y-6" data-testid="admin-franchises">
            {leaders.length > 0 && (
                <div className="space-y-3" data-testid="admin-fr-leaderboard">
                    <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-[#9A7B36]" />
                        <h3 className="font-display text-lg">Dealer leaderboard <span className="text-zinc-400 text-sm">(by attributed revenue)</span></h3>
                    </div>
                    <div className="border border-[#DDD6FE] rounded-sm bg-paper overflow-hidden">
                        {leaders.map((f, i) => (
                            <div key={f.franchise_id} className={`flex items-center justify-between gap-3 px-4 py-3 ${i > 0 ? "border-t border-[#EAE3D4]" : ""}`} data-testid={`admin-fr-leader-${i}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="text-lg w-6 text-center shrink-0">{MEDALS[i] || <span className="text-sm font-mono text-zinc-400">#{i + 1}</span>}</span>
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{f.business_name}</div>
                                        <div className="text-xs text-zinc-500 truncate">{f.territory || "Territory TBD"} · {f.status}</div>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-mono text-sm text-[#4C1D95] font-semibold">{money(f.attributed_revenue_cents)}</div>
                                    <div className="text-xs text-zinc-500">owed {money(f.royalties_owed_cents)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="space-y-3">
            <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#4C1D95]" />
                <h3 className="font-display text-lg">Dealer applications <span className="text-zinc-400 text-sm">({items.length})</span></h3>
            </div>
            {items.length === 0 ? (
                <div className="border border-[#DDD6FE] rounded-sm bg-paper p-10 text-center text-sm text-zinc-500" data-testid="admin-fr-empty">No dealer applications yet.</div>
            ) : items.map((f) => (
                <div key={f.franchise_id} className="border border-[#DDD6FE] rounded-sm bg-paper p-4" data-testid={`admin-fr-${f.franchise_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="font-medium">{f.business_name} <span className="text-xs font-mono uppercase text-zinc-400 border border-zinc-200 rounded-sm px-1 ml-1">{f.status}</span></div>
                            <div className="text-sm text-zinc-600">{f.contact_name} · {f.email} · {f.phone || "no phone"}</div>
                            <div className="text-sm text-zinc-500">{f.territory || "Territory TBD"}{f.franchise_code ? ` · code ${f.franchise_code}` : ""}</div>
                            {f.experience && <div className="text-xs text-zinc-500 mt-1 max-w-xl">{f.experience}</div>}
                            <div className="text-xs text-zinc-500 mt-1 font-mono">
                                agreement {f.agreement_signed ? "signed ✓" : "unsigned"} · license {f.license_paid ? "paid" : `${f.installments_paid}/12`} · onboarding {f.onboarding_paid ? "paid" : "due"} · owed {money(f.royalties_owed_cents)} · attributed {money(f.attributed_revenue_cents)}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {f.status === "pending" && (
                                <>
                                    <Button onClick={() => act(f.franchise_id, "approve")} data-testid={`admin-fr-approve-${f.franchise_id}`} className="bg-[#4C1D95] text-white rounded-sm h-9 text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                                    <Button onClick={() => act(f.franchise_id, "reject")} data-testid={`admin-fr-reject-${f.franchise_id}`} variant="outline" className="rounded-sm h-9 text-xs border-red-200 text-red-600"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                                </>
                            )}
                            {f.status === "approved" && (
                                <Button onClick={() => act(f.franchise_id, "activate")} data-testid={`admin-fr-activate-${f.franchise_id}`} className="bg-brand-green text-white rounded-sm h-9 text-xs">Mark active</Button>
                            )}
                            {(f.status === "approved" || f.status === "active") && (
                                <Button onClick={() => revoke(f.franchise_id, f.business_name)} data-testid={`admin-fr-revoke-${f.franchise_id}`} variant="outline" className="rounded-sm h-9 text-xs border-red-300 text-red-700"><Ban className="w-3.5 h-3.5 mr-1" /> Revoke</Button>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            </div>
        </div>
    );
}
